import {
  Settings,
  ListChecks,
  Database,
  Bug,
  Activity,
  FileText,
  AlertCircle,
  ClipboardCheck,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { TaskListViewer } from "@/components/TaskListViewer";
import { QuestionnaireTestSuite } from "@/components/dev/QuestionnaireTestSuite";
import {
  scanTodoDirectory,
  getPriorityColor,
  getCategoryIcon,
  type TodoListInfo,
} from "@/utils/todoScanner";

interface DevTool {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  available: boolean;
  badge?: string;
}

const devTools: DevTool[] = [
  {
    id: "task-lists",
    title: "Task Lists",
    description: "View and track markdown-based task lists from documentation",
    icon: ListChecks,
    available: true,
  },
  {
    id: "database",
    title: "Database Tools",
    description: "Inspect database schema, run queries, and manage data",
    icon: Database,
    available: false,
    badge: "Coming Soon",
  },
  {
    id: "debug",
    title: "Debug Console",
    description: "View application logs, errors, and debugging information",
    icon: Bug,
    available: false,
    badge: "Coming Soon",
  },
  {
    id: "performance",
    title: "Performance Monitor",
    description: "Track application performance, analytics, and user metrics",
    icon: Activity,
    available: true,
  },
  {
    id: "api-tester",
    title: "API Tester",
    description: "Test API endpoints, inspect requests and responses",
    icon: FileText,
    available: false,
    badge: "Coming Soon",
  },
  {
    id: "questionnaire-tester",
    title: "Questionnaire Tester",
    description:
      "Inspect questionnaire structure, weights, and generate test assessments",
    icon: ClipboardCheck,
    available: true,
  },
];

// Todo lists are now auto-discovered from /docs/todo/ directory

export default function SystemSettingsPage() {
  const navigate = useNavigate();
  const [isDevMode, setIsDevMode] = useState(false);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [selectedTodoInfo, setSelectedTodoInfo] = useState<TodoListInfo | null>(
    null,
  );
  const [markdownContent, setMarkdownContent] = useState<string>("");
  const [todoLists, setTodoLists] = useState<TodoListInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if dev mode is enabled
    const devMode = import.meta.env.VITE_DEV_MODE === "true";
    setIsDevMode(devMode);

    // Redirect to home if not in dev mode
    if (!devMode) {
      navigate("/");
    }
  }, [navigate]);

  // Scan for todo lists when task-lists tool is selected
  useEffect(() => {
    if (selectedTool === "task-lists" && todoLists.length === 0) {
      const loadTodoLists = async () => {
        setIsScanning(true);
        try {
          const lists = await scanTodoDirectory();
          setTodoLists(lists);
        } catch (err) {
          console.error("Error scanning todo directory:", err);
          setError("Failed to scan todo directory");
        } finally {
          setIsScanning(false);
        }
      };
      loadTodoLists();
    }
  }, [selectedTool, todoLists.length]);

  const loadTodoList = async (todoInfo: TodoListInfo) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(todoInfo.path);
      if (!response.ok) {
        throw new Error(`Failed to load file: ${response.statusText}`);
      }
      const content = await response.text();
      setMarkdownContent(content);
      setSelectedFile(todoInfo.path);
      setSelectedTodoInfo(todoInfo);
    } catch (err) {
      console.error("Error loading todo list:", err);
      setError(err instanceof Error ? err.message : "Failed to load file");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isDevMode) {
    return null;
  }

  // Show tool selection
  if (!selectedTool) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Settings className="h-8 w-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">
                System Settings
              </h1>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-full">
                DEV ONLY
              </span>
            </div>
            <p className="text-gray-600">
              Development tools and utilities for debugging and monitoring
            </p>
          </div>

          {/* Dev Tools Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {devTools.map((tool) => {
              const Icon = tool.icon;
              return (
                <button
                  key={tool.id}
                  onClick={() => {
                    if (!tool.available) return;
                    if (tool.id === "performance") {
                      navigate("/admin/analytics");
                    } else {
                      setSelectedTool(tool.id);
                    }
                  }}
                  disabled={!tool.available}
                  className={`relative bg-white rounded-lg shadow-sm border-2 p-6 text-left transition-all ${
                    tool.available
                      ? "border-gray-200 hover:border-blue-500 hover:shadow-md cursor-pointer"
                      : "border-gray-200 opacity-60 cursor-not-allowed"
                  }`}
                >
                  {/* Badge */}
                  {tool.badge && (
                    <span className="absolute top-4 right-4 px-2 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded">
                      {tool.badge}
                    </span>
                  )}

                  {/* Icon */}
                  <div className="mb-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {tool.title}
                  </h3>
                  <p className="text-sm text-gray-600">{tool.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Task Lists Tool
  if (selectedTool === "task-lists") {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => {
                setSelectedTool(null);
                setSelectedFile(null);
                setMarkdownContent("");
              }}
              className="text-blue-600 hover:text-blue-700 mb-4 flex items-center gap-2"
            >
              ← Back to System Settings
            </button>

            <div className="flex items-center gap-3 mb-2">
              <ListChecks className="h-8 w-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">Task Lists</h1>
            </div>
            <p className="text-gray-600">
              View and track progress on markdown-based task lists
            </p>
          </div>

          {/* File Selection */}
          {!selectedFile && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Available Task Lists
                </h2>
                {isScanning && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                    Scanning...
                  </div>
                )}
              </div>

              {todoLists.length === 0 && !isScanning && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                  <p className="text-yellow-800 mb-2">
                    No todo lists found in /docs/todo/
                  </p>
                  <p className="text-sm text-yellow-700">
                    Create a new folder in /docs/todo/ with a todo.md file to
                    get started.
                  </p>
                  <a
                    href="/docs/todo/README.md"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-blue-600 hover:text-blue-700 underline text-sm"
                  >
                    View documentation →
                  </a>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {todoLists.map((todoInfo) => (
                  <button
                    key={todoInfo.path}
                    onClick={() => loadTodoList(todoInfo)}
                    className="bg-white rounded-lg shadow-sm border-2 border-gray-200 hover:border-blue-500 hover:shadow-md p-6 text-left transition-all group"
                  >
                    {/* Header with icon and priority */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">
                          {getCategoryIcon(todoInfo.metadata.category)}
                        </span>
                        {todoInfo.metadata.priority && (
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-semibold ${getPriorityColor(todoInfo.metadata.priority)}`}
                          >
                            {todoInfo.metadata.priority.toUpperCase()}
                          </span>
                        )}
                      </div>
                      {!todoInfo.hasMetadataFile && (
                        <span
                          className="text-xs text-gray-400"
                          title="Auto-generated metadata"
                        >
                          ⚠️
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {todoInfo.metadata.name}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {todoInfo.metadata.description}
                    </p>

                    {/* Tags */}
                    {todoInfo.metadata.tags &&
                      todoInfo.metadata.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {todoInfo.metadata.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                          {todoInfo.metadata.tags.length > 3 && (
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded">
                              +{todoInfo.metadata.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                    {/* Footer */}
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-400 font-mono truncate">
                        {todoInfo.folderName}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading task list...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900 mb-1">
                  Error Loading File
                </h3>
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setError(null);
                  }}
                  className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
                >
                  Try another file
                </button>
              </div>
            </div>
          )}

          {/* Task List Viewer */}
          {selectedFile && markdownContent && !isLoading && !error && (
            <div>
              <button
                onClick={() => {
                  setSelectedFile(null);
                  setSelectedTodoInfo(null);
                  setMarkdownContent("");
                }}
                className="mb-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
              >
                ← Select different todo list
              </button>

              {/* Todo Info Header */}
              {selectedTodoInfo && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-3xl">
                          {getCategoryIcon(selectedTodoInfo.metadata.category)}
                        </span>
                        <h2 className="text-2xl font-bold text-gray-900">
                          {selectedTodoInfo.metadata.name}
                        </h2>
                        {selectedTodoInfo.metadata.priority && (
                          <span
                            className={`text-sm px-3 py-1 rounded-full font-semibold ${getPriorityColor(selectedTodoInfo.metadata.priority)}`}
                          >
                            {selectedTodoInfo.metadata.priority.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-3">
                        {selectedTodoInfo.metadata.description}
                      </p>

                      {/* Metadata Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        {selectedTodoInfo.metadata.owner && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                              Owner
                            </p>
                            <p className="text-sm font-semibold text-gray-900">
                              {selectedTodoInfo.metadata.owner}
                            </p>
                          </div>
                        )}
                        {selectedTodoInfo.metadata.category && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                              Category
                            </p>
                            <p className="text-sm font-semibold text-gray-900 capitalize">
                              {selectedTodoInfo.metadata.category}
                            </p>
                          </div>
                        )}
                        {selectedTodoInfo.metadata.createdAt && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                              Created
                            </p>
                            <p className="text-sm font-semibold text-gray-900">
                              {selectedTodoInfo.metadata.createdAt}
                            </p>
                          </div>
                        )}
                        {selectedTodoInfo.metadata.dueDate && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                              Due Date
                            </p>
                            <p className="text-sm font-semibold text-gray-900">
                              {selectedTodoInfo.metadata.dueDate}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Tags */}
                      {selectedTodoInfo.metadata.tags &&
                        selectedTodoInfo.metadata.tags.length > 0 && (
                          <div className="mt-4">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                              Tags
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {selectedTodoInfo.metadata.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              )}

              <TaskListViewer
                markdownContent={markdownContent}
                fileName={selectedFile}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Questionnaire Tester Tool
  if (selectedTool === "questionnaire-tester") {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => setSelectedTool(null)}
              className="text-blue-600 hover:text-blue-700 mb-4 flex items-center gap-2"
            >
              ← Back to System Settings
            </button>

            <div className="flex items-center gap-3 mb-2">
              <ClipboardCheck className="h-8 w-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">
                Questionnaire Tester
              </h1>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-full">
                DEV ONLY
              </span>
            </div>
            <p className="text-gray-600">
              Inspect questionnaire structure, weights, and generate test
              assessments to preview scoring & reports
            </p>
          </div>

          <QuestionnaireTestSuite />
        </div>
      </div>
    );
  }

  return null;
}
